// Génère un fichier .ics (iCalendar) pour une facture.
// - Titre : informations des Prestations / Courses
// - Date/heure : celles de la 5e ligne de Prestations / Courses (avec repli)
// - Alertes : 2h avant et 1h avant (si une heure est renseignée)
// - Notes : informations du client + prix TTC
// Sur iPhone/iPad, l'ouverture du fichier propose de l'ajouter au Calendrier.

const pad = (n) => String(n).padStart(2, "0");

const toICSDate = (d) => {
  const dt = new Date(d);
  if (isNaN(dt)) return null;
  return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}`;
};

const nextDay = (d) => {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + 1);
  return `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}`;
};

const fmtLocal = (dt) =>
  `${dt.getFullYear()}${pad(dt.getMonth() + 1)}${pad(dt.getDate())}T${pad(dt.getHours())}${pad(dt.getMinutes())}00`;

const stamp = () => {
  const n = new Date();
  return `${n.getUTCFullYear()}${pad(n.getUTCMonth() + 1)}${pad(n.getUTCDate())}T${pad(n.getUTCHours())}${pad(n.getUTCMinutes())}${pad(n.getUTCSeconds())}Z`;
};

const esc = (s = "") => String(s).replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\n/g, "\\n");

// Ligne de référence : 5e ligne de Prestations / Courses, sinon 1ère ligne datée
const pickLine = (inv) => {
  const items = inv.line_items || [];
  if (items[4]) return items[4];
  return items.find((li) => li.date_course) || {};
};

// Titre à partir de l'ensemble des prestations / courses
const buildSummary = (inv) => {
  const items = inv.line_items || [];
  const parts = items
    .map((li) => {
      const label = li.description || "Course";
      const trajet = [li.depart, li.arrivee].filter(Boolean).join(" → ");
      return trajet ? `${label} (${trajet})` : label;
    })
    .filter(Boolean);
  if (parts.length) return parts.join(" ; ");
  return `Facture ${inv.number}`;
};

// Notes : informations client + prix TTC
const buildDescription = (inv) => {
  const c = inv.client || {};
  const montant = new Intl.NumberFormat("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .format(Number(inv.total_ttc || 0)).replace(/[\u202f\u00a0]/g, " ");
  const lines = [];
  if (c.name) lines.push(`Client : ${c.name}`);
  const adr = [c.address, c.postal_city].filter(Boolean).join(", ");
  if (adr) lines.push(adr);
  if (c.phone) lines.push(`Tél : ${c.phone}`);
  if (c.email) lines.push(`E-mail : ${c.email}`);
  lines.push(`Montant TTC : ${montant} €`);
  lines.push(`Facture ${inv.number}`);
  return lines.join("\n");
};

export function addInvoiceToCalendar(inv) {
  const ref = pickLine(inv);
  const dateStr = ref.date_course || inv.date_echeance || inv.date_emission;
  if (!toICSDate(dateStr)) return false;

  const summary = buildSummary(inv);
  const desc = buildDescription(inv);
  const heure = ref.heure_course; // "HH:MM"

  const head = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//MB TRANSPORT//Facturation//FR",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    `UID:facture-${inv.id || inv.number}@mbtransport`,
    `DTSTAMP:${stamp()}`,
  ];

  let timing;
  let alarms;
  if (heure && /^\d{2}:\d{2}/.test(heure)) {
    // Événement daté à l'heure indiquée + rappels 2h et 1h avant
    const start = new Date(`${dateStr}T${heure}`);
    const end = new Date(start.getTime() + 60 * 60 * 1000);
    timing = [`DTSTART:${fmtLocal(start)}`, `DTEND:${fmtLocal(end)}`];
    alarms = [
      "BEGIN:VALARM", "ACTION:DISPLAY", `DESCRIPTION:${esc(summary)}`, "TRIGGER:-PT2H", "END:VALARM",
      "BEGIN:VALARM", "ACTION:DISPLAY", `DESCRIPTION:${esc(summary)}`, "TRIGGER:-PT1H", "END:VALARM",
    ];
  } else {
    // Pas d'heure : événement journée entière + rappel le matin
    timing = [`DTSTART;VALUE=DATE:${toICSDate(dateStr)}`, `DTEND;VALUE=DATE:${nextDay(dateStr)}`];
    alarms = ["BEGIN:VALARM", "ACTION:DISPLAY", `DESCRIPTION:${esc(summary)}`, "TRIGGER:PT9H", "END:VALARM"];
  }

  const lines = [
    ...head,
    ...timing,
    `SUMMARY:${esc(summary)}`,
    `DESCRIPTION:${esc(desc)}`,
    ...alarms,
    "END:VEVENT",
    "END:VCALENDAR",
  ];

  const blob = new Blob([lines.join("\r\n")], { type: "text/calendar;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${inv.number || "facture"}.ics`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
  return true;
}
